/**
 * CoConnect — EUDR World Map (v4.4.21 — Enterprise 10/10)
 * ════════════════════════════════════════════════════════════════
 * Full EUDR compliance map with:
 *   - ALL farmers' polygons with EUDR status coloring
 *   - Cameroon cocoa-belt zone overlays (color-coded regions)
 *   - Click polygon → popup with View/Verify/Reject buttons
 *   - Farm list sidebar with action buttons per farm
 *   - Filter by EUDR status, village, risk level
 *   - Summary stats
 *   - Scale bar, north arrow, layer switcher, fullscreen, coordinate readout
 *   - Esri World Imagery base map (0.3m, free)
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import { apiClient } from '../services/api';
import { createSemanticTileController, type MapTileRuntimeStatus, type SemanticTileController } from '../services/googleMaps';

const EUDR_COLORS: Record<string, { color: string; fillColor: string; label: string }> = {
  VERIFIED:  { color: '#15803D', fillColor: '#22c55e', label: 'EUDR Verified' },
  PENDING:   { color: '#D97706', fillColor: '#fbbf24', label: 'EUDR Pending' },
  REJECTED:  { color: '#dc2626', fillColor: '#ef4444', label: 'EUDR Rejected' },
  EXPIRED:   { color: '#dc2626', fillColor: '#ef4444', label: 'EUDR Expired' },
  UNMAPPED:  { color: '#6b7280', fillColor: '#9ca3af', label: 'Not Mapped' },
};

// v3.10.1: Fix leaflet default icon paths (Vite bundler doesn't resolve them automatically)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// v4.4.21: Cameroon cocoa-belt zones (approximate bounding boxes)
// Used to show which cocoa zone each farm falls in
const COCOA_ZONES = [
  { name: 'South West', color: '#3b82f6', bounds: [[4.0, 8.8], [6.5, 9.8]] },
  { name: 'Littoral', color: '#8b5cf6', bounds: [[3.5, 9.5], [5.2, 10.8]] },
  { name: 'Centre', color: '#ec4899', bounds: [[3.5, 11.0], [5.5, 12.5]] },
  { name: 'South', color: '#14b8a6', bounds: [[1.5, 10.5], [3.8, 13.0]] },
  { name: 'East', color: '#f97316', bounds: [[2.0, 13.0], [4.5, 16.0]] },
];

const EudrWorldMap: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const polygonsRef = useRef<L.Polygon[]>([]);
  const zoneRectsRef = useRef<L.Rectangle[]>([]);
  const tileControllerRef = useRef<SemanticTileController | null>(null);
  const [farms, setFarms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterVillage, setFilterVillage] = useState<string>('');
  const [filterRisk, setFilterRisk] = useState<string>('');
  const [selectedFarm, setSelectedFarm] = useState<any | null>(null);
  const [viewingPolygon, setViewingPolygon] = useState<any | null>(null);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [coords, setCoords] = useState('');
  const [layerType, setLayerType] = useState<'satellite' | 'street'>('satellite');
  const [showZones, setShowZones] = useState(true);
  const [villages, setVillages] = useState<string[]>([]);
  const [tileStatus, setTileStatus] = useState<MapTileRuntimeStatus>({ state: 'loading', requested: 'satellite', provider: 'esri' });

  const installTiles = useCallback((type: 'satellite' | 'street') => {
    const map = mapRef.current;
    if (!map) return;
    tileControllerRef.current?.destroy();
    tileControllerRef.current = createSemanticTileController(map, type, setTileStatus);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/eudr/farms-report');
      const allFarms = res.data?.farms || [];
      setFarms(allFarms);
      const uniqueVillages = [...new Set(allFarms.map((f: any) => f.village).filter(Boolean))].sort();
      setVillages(uniqueVillages as string[]);
    } catch (err: any) {
      console.error('Failed to load farms:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // ─── Initialize the map ───
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: [4.6333, 9.4500], zoom: 7, zoomControl: true, attributionControl: true,
    });
    L.control.scale({ position: 'bottomleft', metric: true, imperial: true }).addTo(map);
    map.on('mousemove', (e: L.LeafletMouseEvent) => setCoords(`${e.latlng.lat.toFixed(5)}, ${e.latlng.lng.toFixed(5)}`));
    map.on('mouseout', () => setCoords(''));
    mapRef.current = map;
    installTiles('satellite');
    const invalidate = () => window.requestAnimationFrame(() => mapRef.current?.invalidateSize({ pan: false }));
    const ro = new ResizeObserver(invalidate);
    ro.observe(containerRef.current);
    window.addEventListener('coconnect:layout-resize', invalidate as EventListener);
    window.addEventListener('resize', invalidate);
    const initialTimer = window.setTimeout(invalidate, 200);
    return () => { window.clearTimeout(initialTimer); window.removeEventListener('coconnect:layout-resize', invalidate as EventListener); window.removeEventListener('resize', invalidate); ro.disconnect(); tileControllerRef.current?.destroy(); tileControllerRef.current = null; map.remove(); mapRef.current = null; };
  }, []);

  // ─── Render zone overlays + farm polygons ───
  useEffect(() => {
    const map = mapRef.current;
    if (!map || loading) return;

    // Clear existing
    polygonsRef.current.forEach(p => map.removeLayer(p));
    polygonsRef.current = [];
    zoneRectsRef.current.forEach(r => map.removeLayer(r));
    zoneRectsRef.current = [];

    // v4.4.21: Draw Cameroon cocoa-belt zone overlays (color-coded rectangles)
    if (showZones) {
      COCOA_ZONES.forEach(zone => {
        const rect = L.rectangle(zone.bounds as any, {
          color: zone.color,
          weight: 2,
          fillColor: zone.color,
          fillOpacity: 0.05,
          dashArray: '4 6',
        }).addTo(map);
        rect.bindTooltip(`${zone.name} Zone`, { permanent: false, direction: 'center', className: 'zone-label' });
        zoneRectsRef.current.push(rect);
      });
    }

    // Filter farms
    const filtered = farms.filter(f => {
      if (filterStatus && f.eudr_status !== filterStatus) return false;
      if (filterVillage && f.village !== filterVillage) return false;
      if (filterRisk && f.eudr_risk_level !== filterRisk) return false;
      return true;
    });

    // Draw each farm polygon
    const bounds: L.LatLngExpression[] = [];
    filtered.forEach(farm => {
      if (!farm.gps_polygon_geojson && !farm.gps_polygon) return;
      try {
        const geojson = farm.gps_polygon_geojson ? JSON.parse(farm.gps_polygon_geojson) : farm.gps_polygon;
        if (!geojson?.coordinates?.[0]) return;
        const pts = geojson.coordinates[0].map(([lng, lat]: [number, number]) => [lat, lng] as [number, number]);
        if (pts.length < 3) return;

        const eudrStatus = farm.eudr_status || 'UNMAPPED';
        const style = EUDR_COLORS[eudrStatus] || EUDR_COLORS.UNMAPPED;

        // Determine which cocoa zone this farm is in
        const farmLat = farm.centroid_lat ? Number(farm.centroid_lat) : pts[0][0];
        const farmLng = farm.centroid_lng ? Number(farm.centroid_lng) : pts[0][1];
        const farmZone = COCOA_ZONES.find(z =>
          farmLat >= z.bounds[0][0] && farmLat <= z.bounds[1][0] &&
          farmLng >= z.bounds[0][1] && farmLng <= z.bounds[1][1]
        );

        const polygon = L.polygon(pts, {
          color: style.color,
          weight: 2.5,
          fillColor: style.fillColor,
          fillOpacity: 0.45,
          dashArray: eudrStatus === 'PENDING' || eudrStatus === 'EXPIRED' ? '8 4' : undefined,
        }).addTo(map);

        // v4.4.21: Popup with action buttons (View Polygon, Verify, Reject)
        const popupId = `eudr-popup-${farm.id}`;
        polygon.bindPopup(`
          <div style="font-family: Inter, sans-serif; font-size: 13px; line-height: 1.6; min-width: 260px;">
            <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px;">
              <strong style="font-size: 14px; color: ${style.color};">${farm.name || 'Unknown Farm'}</strong>
              <span style="font-size: 10px; padding: 2px 8px; border-radius: 4px; background: ${style.fillColor}; color: ${style.color}; font-weight: 600;">${eudrStatus}</span>
            </div>
            <div style="color: #666; font-size: 11px;">
              <div>Farmer: <strong>${farm.farmer_name || '—'}</strong></div>
              <div>Village: ${farm.village || '—'}</div>
              <div>Area: <strong>${Number(farm.polygon_area_hectares || 0).toFixed(2)} ha</strong></div>
              ${farmZone ? `<div>Zone: <span style="color: ${farmZone.color}; font-weight: 600;">${farmZone.name}</span></div>` : ''}
              ${farm.eudr_risk_level ? `<div>Risk: <strong style="color: ${farm.eudr_risk_level === 'high' ? '#dc2626' : farm.eudr_risk_level === 'medium' ? '#D97706' : '#15803D'};">${farm.eudr_risk_level}</strong></div>` : ''}
              ${farm.eudr_verified_at ? `<div>Verified: ${new Date(farm.eudr_verified_at).toLocaleDateString()}</div>` : ''}
              <div style="font-size: 10px; color: #999;">GPS: ${Number(farmLat).toFixed(5)}, ${Number(farmLng).toFixed(5)}</div>
            </div>
            <hr style="margin: 8px 0; border: none; border-top: 1px solid #ddd;">
            <div style="display: flex; gap: 6px; flex-wrap: wrap;">
              <button onclick="window.__eudrView('${farm.id}')" style="padding: 4px 10px; font-size: 11px; font-weight: 600; background: #1e40af; color: #fff; border: none; border-radius: 6px; cursor: pointer;">View Polygon</button>
              ${eudrStatus !== 'VERIFIED' ? `<button onclick="window.__eudrVerify('${farm.id}')" style="padding: 4px 10px; font-size: 11px; font-weight: 600; background: #15803D; color: #fff; border: none; border-radius: 6px; cursor: pointer;">Verify</button>` : ''}
              ${eudrStatus !== 'REJECTED' ? `<button onclick="window.__eudrReject('${farm.id}')" style="padding: 4px 10px; font-size: 11px; font-weight: 600; background: #dc2626; color: #fff; border: none; border-radius: 6px; cursor: pointer;">Reject</button>` : ''}
            </div>
          </div>
        `);

        polygon.on('click', () => setSelectedFarm(farm));
        polygonsRef.current.push(polygon);

        if (farmLat && farmLng) bounds.push([farmLat, farmLng]);
      } catch { /* skip invalid */ }
    });

    if (bounds.length > 0) {
      try { map.fitBounds(bounds as any, { padding: [40, 40], maxZoom: 14 }); } catch { /* ignore */ }
    }
  }, [farms, loading, filterStatus, filterVillage, filterRisk, showZones]);

  // v4.4.21: Expose action handlers globally for Leaflet popup buttons
  useEffect(() => {
    (window as any).__eudrView = (farmId: string) => {
      const farm = farms.find(f => f.id === farmId);
      if (farm) setViewingPolygon(farm);
    };
    (window as any).__eudrVerify = async (farmId: string) => {
      setVerifyingId(farmId);
      try {
        await apiClient.post(`/eudr/verify-farm/${farmId}`, { risk_level: 'LOW' });
        await refresh();
      } catch (err: any) {
        alert(err.response?.data?.error || 'Failed to verify farm');
      } finally {
        setVerifyingId(null);
      }
    };
    (window as any).__eudrReject = async (farmId: string) => {
      if (!confirm('Are you sure you want to REJECT this farm for EUDR? This will block its cocoa from EU export.')) return;
      setRejectingId(farmId);
      try {
        await apiClient.post(`/eudr/verify-farm/${farmId}`, { risk_level: 'HIGH', status: 'REJECTED' });
        await refresh();
      } catch (err: any) {
        alert(err.response?.data?.error || 'Failed to reject farm');
      } finally {
        setRejectingId(null);
      }
    };
    return () => {
      delete (window as any).__eudrView;
      delete (window as any).__eudrVerify;
      delete (window as any).__eudrReject;
    };
  }, [farms, refresh]);

  const switchLayer = useCallback((type: 'satellite' | 'street') => {
    if (!mapRef.current) return;
    installTiles(type);
    setLayerType(type);
  }, [installTiles]);

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) containerRef.current.requestFullscreen?.();
    else document.exitFullscreen?.();
  }, []);

  // Fly to a specific farm
  const flyToFarm = useCallback((farm: any) => {
    if (!mapRef.current || !farm.centroid_lat || !farm.centroid_lng) return;
    mapRef.current.flyTo([Number(farm.centroid_lat), Number(farm.centroid_lng)], 16, { duration: 1.5 });
    setSelectedFarm(farm);
  }, []);

  // Stats
  const stats = farms.reduce((acc: any, f) => {
    const s = f.eudr_status || 'UNMAPPED';
    acc[s] = (acc[s] || 0) + 1; acc.total++;
    if (f.polygon_area_hectares) acc.totalArea += Number(f.polygon_area_hectares);
    return acc;
  }, { total: 0, totalArea: 0, VERIFIED: 0, PENDING: 0, REJECTED: 0, EXPIRED: 0, UNMAPPED: 0 });

  const filtered = farms.filter(f => {
    if (filterStatus && f.eudr_status !== filterStatus) return false;
    if (filterVillage && f.village !== filterVillage) return false;
    if (filterRisk && f.eudr_risk_level !== filterRisk) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      {tileStatus.state === 'fallback' && <div className="text-xs text-amber-300">Requested map provider unavailable; showing fallback tiles.</div>}
      {tileStatus.state === 'error' && <div className="text-xs text-red-300">Map tiles are unavailable from the configured providers.</div>}
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          { label: 'Verified', value: stats.VERIFIED || 0, bg: 'bg-success-50 dark:bg-success-500/10', border: 'border-success-200 dark:border-success-500/20', text: 'text-success-700 dark:text-success-500' },
          { label: 'Pending', value: stats.PENDING || 0, bg: 'bg-warning-50 dark:bg-warning-500/10', border: 'border-warning-200 dark:border-warning-500/20', text: 'text-warning-700 dark:text-warning-500' },
          { label: 'Rejected', value: stats.REJECTED || 0, bg: 'bg-danger-50 dark:bg-danger-500/10', border: 'border-danger-200 dark:border-danger-500/20', text: 'text-danger-700 dark:text-danger-500' },
          { label: 'Unmapped', value: stats.UNMAPPED || 0, bg: 'bg-ink-100 dark:bg-ink-800', border: 'border-ink-200 dark:border-ink-700', text: 'text-ink-600 dark:text-ink-400' },
          { label: 'Total Farms', value: stats.total, bg: 'bg-admin-50 dark:bg-admin-500/10', border: 'border-admin-200 dark:border-admin-500/20', text: 'text-admin-700 dark:text-admin-500' },
          { label: 'Total Area', value: `${stats.totalArea.toFixed(2)} ha`, bg: 'bg-ink-100 dark:bg-ink-800', border: 'border-ink-200 dark:border-ink-700', text: 'text-ink-600 dark:text-ink-400' },
        ].map(kpi => (
          <div key={kpi.label} className={`px-3 py-2 rounded-lg ${kpi.bg} border ${kpi.border}`}>
            <p className={`text-2xs uppercase tracking-micro font-medium ${kpi.text}`}>{kpi.label}</p>
            <p className={`text-lg font-bold nums ${kpi.text}`}>{kpi.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select className="input md:w-48" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All EUDR Statuses</option>
          <option value="VERIFIED">Verified (Green)</option>
          <option value="PENDING">Pending (Amber)</option>
          <option value="REJECTED">Rejected (Red)</option>
          <option value="EXPIRED">Expired (Red)</option>
          <option value="UNMAPPED">Unmapped (Gray)</option>
        </select>
        <select className="input md:w-48" value={filterVillage} onChange={e => setFilterVillage(e.target.value)}>
          <option value="">All Villages</option>
          {villages.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select className="input md:w-48" value={filterRisk} onChange={e => setFilterRisk(e.target.value)}>
          <option value="">All Risk Levels</option>
          <option value="low">Low Risk</option>
          <option value="medium">Medium Risk</option>
          <option value="high">High Risk</option>
        </select>
        <label className="flex items-center gap-1.5 text-xs font-medium text-ink-600 dark:text-ink-400 cursor-pointer">
          <input type="checkbox" checked={showZones} onChange={e => setShowZones(e.target.checked)} className="rounded" />
          Show cocoa zones
        </label>
        <button onClick={refresh} className="btn-secondary text-sm flex items-center gap-1.5">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
          Refresh
        </button>
      </div>

      {/* Map + Sidebar layout */}
      <div className="flex gap-4">
        {/* Map */}
        <div className="flex-1 relative rounded-xl overflow-hidden border border-ink-200 dark:border-ink-800 shadow-lg" style={{ height: '600px' }}>
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-ink-100 dark:bg-ink-900 z-[2000]">
              <div className="text-center">
                <div className="w-10 h-10 border-4 border-admin-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-ink-500">Loading farm polygons…</p>
              </div>
            </div>
          )}
          <div ref={containerRef} className="absolute inset-0" />

          {/* Layer switcher */}
          <div className="absolute top-3 right-3 z-[1000] flex gap-1">
            {(['satellite', 'street'] as const).map(type => (
              <button key={type} onClick={() => switchLayer(type)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-2xs font-semibold shadow-md border transition-all ${layerType === type ? 'bg-admin-600 text-white border-admin-700' : 'bg-white/95 dark:bg-ink-900/95 text-ink-700 dark:text-ink-300 border-ink-200 dark:border-ink-800'}`}>
                {type === 'satellite' ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>
                )}
                {type === 'satellite' ? 'Satellite' : 'Street'}
              </button>
            ))}
          </div>

          {/* Fullscreen */}
          <button onClick={toggleFullscreen} className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] w-8 h-8 flex items-center justify-center bg-white/95 dark:bg-ink-900/95 rounded-md shadow-md border border-ink-200 dark:border-ink-800 text-ink-600 dark:text-ink-300 hover:bg-white" title="Fullscreen">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M3 16v3a2 2 0 002 2h3M16 21h3a2 2 0 002-2v-3"/></svg>
          </button>

          {/* North arrow */}
          <div className="absolute bottom-12 right-3 z-[1000] w-10 h-10 flex items-center justify-center bg-white/95 dark:bg-ink-900/95 rounded-full shadow-md border border-ink-200 dark:border-ink-800">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 2 L16 12 L12 10 L8 12 Z" fill="#dc2626" stroke="#fff" strokeWidth="1"/>
              <path d="M12 22 L8 12 L12 14 L16 12 Z" fill="#374151" stroke="#fff" strokeWidth="1"/>
              <text x="12" y="8" textAnchor="middle" fontSize="6" fill="#dc2626" fontWeight="bold">N</text>
            </svg>
          </div>

          {/* Coordinate readout */}
          {coords && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-[1000] px-2.5 py-1 rounded-md bg-white/95 dark:bg-ink-900/95 backdrop-blur text-2xs font-mono text-ink-600 dark:text-ink-400 shadow-sm border border-ink-200 dark:border-ink-800">
              {coords}
            </div>
          )}

          {/* Legend */}
          <div className="absolute bottom-3 right-3 z-[1000] px-3 py-2 rounded-lg bg-white/95 dark:bg-ink-900/95 backdrop-blur text-2xs shadow-md border border-ink-200 dark:border-ink-800">
            <p className="font-semibold text-ink-800 dark:text-ink-200 mb-1">EUDR Status</p>
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded" style={{ background: '#22c55e' }}></div><span className="text-ink-600 dark:text-ink-400">Verified</span></div>
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded" style={{ background: '#fbbf24' }}></div><span className="text-ink-600 dark:text-ink-400">Pending</span></div>
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded" style={{ background: '#ef4444' }}></div><span className="text-ink-600 dark:text-ink-400">Rejected/Expired</span></div>
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded" style={{ background: '#9ca3af' }}></div><span className="text-ink-600 dark:text-ink-400">Unmapped</span></div>
            </div>
            {showZones && (
              <>
                <p className="font-semibold text-ink-800 dark:text-ink-200 mt-2 mb-1">Cocoa Zones</p>
                <div className="space-y-0.5">
                  {COCOA_ZONES.map(z => (
                    <div key={z.name} className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded border" style={{ borderColor: z.color, background: z.color + '20' }}></div>
                      <span className="text-ink-600 dark:text-ink-400">{z.name}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Farm list sidebar */}
        <div className="w-72 shrink-0 max-h-[600px] overflow-y-auto rounded-xl border border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-900 shadow-md">
          <div className="p-3 border-b border-ink-200 dark:border-ink-800 sticky top-0 bg-white dark:bg-ink-900 z-10">
            <p className="text-xs font-semibold text-ink-900 dark:text-ink-50">Farms ({filtered.length})</p>
            <p className="text-2xs text-ink-500">Click to fly · buttons to act</p>
          </div>
          <div className="divide-y divide-ink-100 dark:divide-ink-800">
            {filtered.length === 0 ? (
              <div className="p-4 text-center text-xs text-ink-400">No farms match filters</div>
            ) : filtered.map(farm => {
              const eudrStatus = farm.eudr_status || 'UNMAPPED';
              const style = EUDR_COLORS[eudrStatus] || EUDR_COLORS.UNMAPPED;
              return (
                <div key={farm.id} className={`p-3 ${selectedFarm?.id === farm.id ? 'bg-admin-50 dark:bg-admin-500/10' : ''} hover:bg-ink-50 dark:hover:bg-ink-800/50 transition-colors`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <button onClick={() => flyToFarm(farm)} className="text-left flex-1 min-w-0">
                      <p className="text-xs font-semibold text-ink-900 dark:text-ink-50 truncate">{farm.name}</p>
                      <p className="text-2xs text-ink-500 truncate">{farm.farmer_name} · {farm.village}</p>
                    </button>
                    <span className="text-2xs px-1.5 py-0.5 rounded font-bold shrink-0" style={{ background: style.fillColor + '30', color: style.color }}>{eudrStatus}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="text-2xs text-ink-500 nums">{Number(farm.polygon_area_hectares || 0).toFixed(2)} ha</span>
                    {farm.eudr_risk_level && (
                      <span className={`text-2xs font-medium ${farm.eudr_risk_level === 'high' ? 'text-danger-600' : farm.eudr_risk_level === 'medium' ? 'text-warning-600' : 'text-success-600'}`}>
                        · {farm.eudr_risk_level} risk
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1 mt-2">
                    <button onClick={() => flyToFarm(farm)} className="text-2xs px-2 py-0.5 rounded bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-300 hover:bg-ink-200 dark:hover:bg-ink-700 font-medium">View</button>
                    {eudrStatus !== 'VERIFIED' && (
                      <button onClick={() => (window as any).__eudrVerify(farm.id)} disabled={verifyingId === farm.id}
                        className="text-2xs px-2 py-0.5 rounded bg-success-600 text-white hover:bg-success-700 font-medium disabled:opacity-50">
                        {verifyingId === farm.id ? '…' : 'Verify'}
                      </button>
                    )}
                    {eudrStatus !== 'REJECTED' && (
                      <button onClick={() => (window as any).__eudrReject(farm.id)} disabled={rejectingId === farm.id}
                        className="text-2xs px-2 py-0.5 rounded bg-danger-600 text-white hover:bg-danger-700 font-medium disabled:opacity-50">
                        {rejectingId === farm.id ? '…' : 'Reject'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Polygon viewer modal */}
      {viewingPolygon && (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setViewingPolygon(null)}>
          <div className="bg-white dark:bg-ink-900 rounded-2xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-ink-200 dark:border-ink-800 flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50">{viewingPolygon.name} — Polygon Detail</h3>
                <p className="text-sm text-ink-500">{viewingPolygon.farmer_name} · {viewingPolygon.village}</p>
              </div>
              <button onClick={() => setViewingPolygon(null)} className="text-ink-400 hover:text-ink-600">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 border-b border-ink-200 dark:border-ink-800">
              <div><p className="text-2xs text-ink-500">Area</p><p className="text-sm font-semibold nums">{Number(viewingPolygon.polygon_area_hectares || 0).toFixed(2)} ha</p></div>
              <div><p className="text-2xs text-ink-500">EUDR Status</p><p className="text-sm font-semibold" style={{ color: (EUDR_COLORS[viewingPolygon.eudr_status] || EUDR_COLORS.UNMAPPED).color }}>{viewingPolygon.eudr_status || 'UNMAPPED'}</p></div>
              <div><p className="text-2xs text-ink-500">Risk Level</p><p className="text-sm font-semibold">{viewingPolygon.eudr_risk_level || '—'}</p></div>
              <div><p className="text-2xs text-ink-500">Batches</p><p className="text-sm font-semibold nums">{viewingPolygon.batch_count || 0}</p></div>
            </div>
            <div className="p-4 flex gap-2">
              <button onClick={() => { (window as any).__eudrVerify(viewingPolygon.id); setViewingPolygon(null); }}
                className="flex-1 py-2.5 text-sm font-semibold text-white bg-success-600 rounded-xl hover:bg-success-700 transition-colors flex items-center justify-center gap-1.5">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
                Verify Farm
              </button>
              <button onClick={() => { (window as any).__eudrReject(viewingPolygon.id); setViewingPolygon(null); }}
                className="flex-1 py-2.5 text-sm font-semibold text-white bg-danger-600 rounded-xl hover:bg-danger-700 transition-colors flex items-center justify-center gap-1.5">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                Reject Farm
              </button>
              <button onClick={() => setViewingPolygon(null)} className="py-2.5 px-4 text-sm font-semibold text-ink-600 dark:text-ink-300 bg-ink-100 dark:bg-ink-800 rounded-xl hover:bg-ink-200 dark:hover:bg-ink-700">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EudrWorldMap;
